import React from 'react'
import styled from 'styled-components'

// Recharts ships an awkwardly evolving `TooltipProps` type across majors. We only
// need a narrow read of `active` + `payload[]` + `label`, so define a minimal shape
// locally rather than chase the upstream typings.
interface ChartTooltipPayloadEntry {
  dataKey?: string | number
  name?: string | number
  value?: number | string | (string | number)[]
  color?: string
  /** Recharts populates this on Pie/Donut slices as a 0..1 fraction of the
   * total. Absent on bar/line charts — the renderer treats it as optional. */
  percent?: number
  /** The full data record for the hovered point (recharts attaches it to every
   * entry). Read by `totalKey` to surface a per-point total the series don't carry. */
  payload?: Record<string, unknown>
}

export interface ChartTooltipRenderProps {
  active?: boolean
  payload?: ChartTooltipPayloadEntry[]
  /** The category-axis value for the hovered datum. Recharts populates this from
   * the chart's `dataKey` (or PieChart `nameKey`). */
  label?: string | number
}

const TooltipBody = styled.div`
  ${({ theme }) => `
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.gray90};
    border-radius: ${theme.borderRadii.large};
    padding: 8px 10px;
    box-shadow: ${theme.boxShadows.small};
    min-width: 160px;
    // Size to the longest line instead of the chart wrapper's width. Recharts
    // renders the tooltip inline inside the chart container, so without this an
    // absolutely-positioned tooltip is bounded by the card and a long label
    // (e.g. a full org name) wraps/clips at the card edge instead of extending
    // over the neighbouring chart (NEX-681). The bar chart already grants it the
    // z-index + escape-viewBox to paint on top; the cap keeps a very long name
    // from running off-screen.
    width: max-content;
    max-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  `}
`

const TooltipHeader = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: ${theme.fontSizes.caption};
    font-weight: ${theme.fontWeights.semiBold};
    color: ${theme.colors.gray600};
  `}
`

const TooltipHeaderLabel = styled.span`
  flex: 1;
  // Let the label shrink below its content width (flex items default to
  // min-width: auto) and let an unbreakable single-word label (e.g. a long org
  // name with no spaces) wrap within the tooltip's max-width instead of
  // overflowing the box (NEX-815). Multi-word names already wrap on spaces.
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: left;
`

// The period/category total sits to the right of the header label (e.g.
// "May 2025 · A$12M"), so a stacked series chart shows the stack height at the
// hovered point without the reader summing the rows by eye.
const TooltipHeaderTotal = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
`

// Row text mirrors the chart legend item (caption size, medium weight,
// gray600, 1rem line-height) so the swatch + label reads identically in the
// tooltip and the legend.
const StyledTooltipRow = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${theme.fontSizes.caption};
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.colors.gray600};
    line-height: 1rem;
  `}
`

// Matches the chart legend swatch (10×10, 2px radius — Figma node 5676:1946)
// so the colour chip is identical across the tooltip and the legend.
const TooltipSwatch = styled.span<{ $color: string }>`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 0.125rem;
  flex-shrink: 0;
  background-color: ${({ $color }) => $color};
`

const TooltipRowLabel = styled.span`
  flex: 1;
  // See TooltipHeaderLabel: allow a long single-word row label (a donut slice's
  // org name) to wrap within the tooltip rather than overflow it (NEX-815).
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: left;
`

const TooltipRowValue = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
`

interface TooltipRowProps {
  swatchColor: string
  label: string
  value: string
}

const TooltipRow = ({ swatchColor, label, value }: TooltipRowProps) => (
  <StyledTooltipRow>
    <TooltipSwatch $color={swatchColor} />
    <TooltipRowLabel>{label}</TooltipRowLabel>
    <TooltipRowValue>{value}</TooltipRowValue>
  </StyledTooltipRow>
)

// Custom tooltip body shared by every LemonadeChart kind. Each row mirrors the
// chart legend (coloured swatch + label) and adds a right-aligned value column.
// No `label: value` separator — the row reads as a key/value pair via the
// flex column layout so all charts present hover info identically.
//
// Defined at module scope so its component identity is stable across chart
// re-renders. Recharts uses reference equality on the `content` element to
// decide whether to remount the tooltip; passing `<Tooltip content={<ChartTooltipBody … />}>`
// (element form) lets recharts merge active/payload/label onto a stable element.
interface ChartTooltipBodyProps extends ChartTooltipRenderProps {
  formatter?: (v: number) => string
  /** Per-series value formatter, dispatched by the payload entry's `dataKey`.
   * Takes precedence over `formatter` — lets a multi-unit chart (e.g. a dual-axis
   * line: a count series + a money series) format each row by its own unit. */
  valueFormatter?: (value: number, dataKey: string) => string
  /** Sum of all data points; when set, each numeric row value is prefixed with
   * its share (e.g. `93% · A$12M`). Pie/donut charts compute and pass it here
   * because Recharts' Pie tooltip payload doesn't carry `percent`. */
  total?: number
  /** Keep the payload's declared series order instead of sorting rows by value.
   * Dual-axis charts mix units (a count and a money amount), so magnitude order
   * is meaningless — preserve the series order the consumer declared. */
  disableSort?: boolean
  /** Data-record key holding the hovered point's total (e.g. the backend row total
   * baked into each datum). When set and the record carries a numeric value there,
   * it renders next to the header label — the stack height for a stacked chart.
   * `valueFormatter` is called with this key as its `dataKey`, so a multi-unit chart
   * can format the total in the total's own unit. */
  totalKey?: string
  /** Formats the header (category) label — e.g. a timeseries x-axis whose value
   * is a raw ISO date the chart renders as `Jan 2025`. */
  labelFormatter?: (label: string) => string
}

const numericValue = (value: ChartTooltipPayloadEntry['value']): number =>
  typeof value === 'number' ? value : 0

// Per-series formatter wins, then the chart-wide formatter, else the raw value
// stringified. Non-numeric values are passed through as a string.
const formatRowValue = (
  raw: ChartTooltipPayloadEntry['value'],
  dataKey: string,
  valueFormatter?: ChartTooltipBodyProps['valueFormatter'],
  formatter?: ChartTooltipBodyProps['formatter']
): string => {
  if (typeof raw !== 'number') return String(raw ?? '')
  if (valueFormatter) return valueFormatter(raw, dataKey)
  if (formatter) return formatter(raw)
  return String(raw)
}

// Prefer the payload's own `percent` (pie/donut); otherwise derive it from the
// running total when one is supplied. `undefined` means "don't show a share".
const computePercent = (
  entry: ChartTooltipPayloadEntry,
  total: number | undefined
): number | undefined => {
  if (typeof entry.percent === 'number') return entry.percent
  const raw = entry.value
  if (typeof raw === 'number' && total != null && total > 0) return raw / total
  return undefined
}

export const ChartTooltipBody = ({
  active,
  payload,
  label,
  formatter,
  valueFormatter,
  total,
  disableSort,
  totalKey,
  labelFormatter,
}: ChartTooltipBodyProps) => {
  if (!active || !payload || payload.length === 0) return null

  // Hide zero-value rows; order the remainder by largest first so the hovered
  // datum's most material contributors sit at the top — unless `disableSort`
  // (dual-axis charts mix units, so keep the declared series order).
  // Treat 0/null/undefined/'' as "no contribution" — mirrors the bar-chart shape
  // predicate (`!props.payload?.[n]`) so a series that collapsed visually also
  // disappears from the hover state.
  const present = payload.filter((entry) => Boolean(entry.value))
  const visible = disableSort
    ? present
    : [...present].sort((a, b) => numericValue(b.value) - numericValue(a.value))

  // The hovered point's total, read from the data record under `totalKey` (the
  // authoritative backend row total — recharts hands the same record to every
  // entry, so any entry will do, including when every series filtered out above).
  // Formatted like a row but dispatched under `totalKey` itself, so a multi-unit
  // chart can format the total in its own unit rather than inheriting whichever
  // series happens to come first.
  const rawTotal = totalKey ? payload[0]?.payload?.[totalKey] : undefined
  const totalValue =
    typeof rawTotal === 'number' && totalKey
      ? formatRowValue(rawTotal, totalKey, valueFormatter, formatter)
      : undefined

  // Nothing to say about this point. A total on its own is still worth showing:
  // under a topN window every rendered series can be zero for a bucket whose real
  // total isn't, and suppressing the tooltip there would hide the one number the
  // backend total exists to guarantee.
  if (visible.length === 0 && totalValue === undefined) return null

  const hasLabel = label !== undefined && label !== ''
  const headerLabel = hasLabel
    ? (labelFormatter ?? String)(String(label))
    : undefined

  return (
    <TooltipBody>
      {(hasLabel || totalValue !== undefined) && (
        <TooltipHeader>
          <TooltipHeaderLabel>{headerLabel}</TooltipHeaderLabel>
          {totalValue !== undefined && (
            <TooltipHeaderTotal>{totalValue}</TooltipHeaderTotal>
          )}
        </TooltipHeader>
      )}
      {visible.map((entry) => {
        const dataKey = String(entry.dataKey ?? entry.name ?? '')
        const formatted = formatRowValue(
          entry.value,
          dataKey,
          valueFormatter,
          formatter
        )
        const percent = computePercent(entry, total)
        const value =
          percent === undefined
            ? formatted
            : `${Math.round(percent * 100)}% · ${formatted}`

        return (
          <TooltipRow
            key={String(entry.dataKey ?? entry.name)}
            swatchColor={String(entry.color ?? '#000')}
            label={String(entry.name ?? entry.dataKey ?? '')}
            value={value}
          />
        )
      })}
    </TooltipBody>
  )
}
ChartTooltipBody.displayName = 'LemonadeChartTooltip'
